import { Button, Tooltip } from "antd";
import React from "react";
import styled from "styled-components";
import { colors } from "styles/colors";
import type { TooltipProps } from "antd";

const StyledButton = styled(Button)`
  &&,
  &:focus:not(:hover) {
    /*
      Match default state when focused. This is a workaround for antd v4, where
      buttons retain focus after click. Remove &:focus... selector after upgrade.
    */
    color: ${colors.GREY_300};
    border-color: transparent;
    background-color: unset;
  }

  display: flex;
  justify-content: center;
  align-items: center;

  /* override antd specificity */
  && {
    box-shadow: none;
    display: flex;
  }

  &[data-size="mini"] {
    width: 16px;
    height: 16px;
  }

  &:hover {
    color: ${colors.GREY_500};
    background-color: ${colors.GREY_25};
    &[data-size="small"] {
      border-color: ${colors.GREY_100};
    }
  }

  &:active,
  &[data-active="true"] {
    color: ${colors.GREY_500};
    background-color: ${colors.GREY_50};
    &[data-size="small"] {
      border-color: ${colors.GREY_100};
    }
  }
  &:disabled {
    color: ${colors.GREY_200};
    background-color: unset; // override antd
    cursor: pointer;
    &[data-size="small"] {
      border-color: transparent; // override antd
    }
  }
`;

export type TooltipPlacement = TooltipProps["placement"];
export interface IconButtonProps extends Pick<
  React.ComponentProps<"button">,
  "className" | "style" | "id"
> {
  icon: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  title?: TooltipProps["title"];
  "data-test"?: string;
  size?: "small" | "mini"; // mini is 16x16, small is roughly 24x24
  disabled?: boolean;
  ref?: React.Ref<HTMLButtonElement>;
  tooltipPlacement?: TooltipPlacement;
  active?: boolean;
  name?: string;
}

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      className,
      title,
      disabled,
      tooltipPlacement = "left",
      active,
      size = "small",
      ...rest
    },
    ref,
  ) => {
    return (
      <Tooltip title={title} placement={tooltipPlacement} mouseEnterDelay={0.5}>
        <StyledButton
          className={className ?? ""}
          ref={ref}
          data-test={rest["data-test"]}
          disabled={disabled}
          data-active={!!active}
          size="small"
          data-size={size}
          data-disabled={disabled}
          {...rest}
        />
      </Tooltip>
    );
  },
);
IconButton.displayName = "IconButton";

export default IconButton;
